import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Row, Col, Form } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import Content from 'layouts/Content';
import {
  Button,
  ButtonExport,
  ButtonFilter,
  InputForm,
  SelectForm,
} from 'components';
import DatePickerForm from 'components/DatePicker/DatePickerForm';
import { useTitle } from 'contexts/TitleProvider';
import Table from 'antd/es/table';
import dayjs from 'dayjs';
import AdjustDebtModal, {
  AdjustDebtModalRef,
} from './components/AdjustDebtModal';
import DebtApis from 'apis/DebtApis';
import { useMutation } from '@tanstack/react-query';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import formatNumber from 'utils/constants/functions';
import { PlantApi } from 'apis/PlantApis';
import { DefaultOptionType } from 'antd/es/select';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import LazySelectForm, {
  initLazySelectData,
  ILazySelectData,
  ILazySelectParams,
} from 'components/Select/LazySelectForm';
import { CustomerApis } from 'apis/CustomerApis';
import { EyeIcon } from 'assets/svg';

const DEFAULT_PAGE_SIZE = 10;

export default function DebtDMSDetailPage() {
  const { setTitle, setBreadcrumb } = useTitle();
  const navigate = useNavigate();
  const location = useLocation();
  const [openFilter, setOpenFilter] = useState(true);
  const [form] = Form.useForm();
  const [data, setData] = useState<any[]>([]);
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    itemsPerPage: DEFAULT_PAGE_SIZE,
    totalItems: 0,
  });
  const [exportLoading, setExportLoading] = useState(false);
  const adjustRef = useRef<AdjustDebtModalRef>(null);

  const [plantOptions, setPlantOptions] = useState<DefaultOptionType[]>([]);
  const [loadingPlant, setLoadingPlant] = useState(false);
  const [teamOptions, setTeamOptions] =
    useState<ILazySelectData>(initLazySelectData);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [customerOptions, setCustomerOptions] =
    useState<ILazySelectData>(initLazySelectData);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const columns: any[] = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 80,
      align: 'center',
    },
    {
      title: 'Chi nhánh',
      dataIndex: 'plantId',
      width: 110,
      align: 'center',
    },
    { title: 'Đội', dataIndex: 'teamSaleId', width: 110, align: 'center' },
    { title: 'Tên đội', dataIndex: 'teamName', width: 180, align: 'center' },
    { title: 'Mã KH', dataIndex: 'sapCustomerId', width: 120, align: 'center' },
    { title: 'Tên KH', dataIndex: 'storeName', width: 220, align: 'center' },
    {
      title: 'Mã cha',
      dataIndex: 'mainCustomerId',
      width: 150,
      align: 'center',
    },
    {
      title: 'Tên KH cha',
      dataIndex: 'mainCustomerName',
      width: 220,
      align: 'center',
    },
    {
      title: 'So sánh',
      dataIndex: 'compare',
      width: 90,
      align: 'center',
      render: (v: any) =>
        v === 'Lệch' ? <span style={{ color: 'red' }}>Lệch</span> : v,
    },
    {
      title: 'Hạn mức Q10 truyền thống',
      align: 'center',
      children: [
        {
          title: 'Q10',
          dataIndex: 'goldThreadQ10Traditional',
          width: 90,
          align: 'right',
        },
        {
          title: 'Ngày',
          dataIndex: 'dateQ10Traditional',
          width: 90,
          align: 'right',
        },
      ],
    },
    {
      title: 'Hạn mức nợ Tiền công Hàng truyền thống',
      align: 'center',
      children: [
        {
          title: 'VND',
          dataIndex: 'amountWagesTraditional',
          width: 140,
          align: 'right',
        },
        {
          title: 'Ngày',
          dataIndex: 'dateWagesTraditional',
          width: 90,
          align: 'right',
        },
      ],
    },
    {
      title: 'Hạn mức nợ Hàng cao cấp',
      align: 'center',
      children: [
        {
          title: 'VND',
          dataIndex: 'amountHighendLimit',
          width: 140,
          align: 'right',
        },
        {
          title: 'Ngày',
          dataIndex: 'dateHighendLimit',
          width: 90,
          align: 'right',
        },
      ],
    },
    {
      title: 'Hạn mức nợ Hàng cao cấp và Tiền công Hàng truyền thống',
      align: 'center',
      children: [
        {
          title: 'VND',
          dataIndex: 'amountHighendTraditionalLimit',
          width: 160,
          align: 'right',
        },
        {
          title: 'Ngày',
          dataIndex: 'dateHighendTraditionalLimit',
          width: 90,
          align: 'right',
        },
      ],
    },
    {
      title: 'PNJP phải thu KH',
      align: 'center',
      children: [
        {
          title: 'Q10',
          dataIndex: 'dmsCollectDebitQ10',
          width: 100,
          align: 'right',
        },
        {
          title: 'VND',
          dataIndex: 'dmsCollectDebitAmount',
          width: 150,
          align: 'right',
        },
      ],
    },
    {
      title: 'PNJP phải trả KH',
      align: 'center',
      children: [
        {
          title: 'Q10',
          dataIndex: 'dmsPayDebitQ10',
          width: 100,
          align: 'right',
        },
        {
          title: 'VND',
          dataIndex: 'dmsPayDebitAmount',
          width: 150,
          align: 'right',
        },
      ],
    },
    {
      title: 'Nguyên liệu giữ hộ Khách',
      align: 'center',
      children: [
        {
          title: 'Q10',
          dataIndex: 'holdingProductQ10',
          width: 150,
          align: 'right',
        },
      ],
    },
    {
      title: 'Nguyên liệu trả Khách',
      align: 'center',
      children: [
        {
          title: 'Q10',
          dataIndex: 'returnProductQ10',
          width: 140,
          align: 'right',
        },
      ],
    },
    {
      title: 'Tuổi nợ',
      align: 'center',
      children: [
        {
          title: 'Dài nhất',
          dataIndex: 'longestDebtAge',
          width: 100,
          align: 'right',
        },
        {
          title: 'Ngắn nhất',
          dataIndex: 'shortestDebtAge',
          width: 150,
          align: 'right',
        },
        {
          title: 'Trung bình',
          dataIndex: 'averageDebtAge',
          width: 120,
          align: 'right',
        },
      ],
    },
    {
      title: 'DMS: NL đã nhận chưa tạo PO (Q10)',
      dataIndex: 'receivedProductPoQ10',
      width: 180,
      align: 'right',
    },
    {
      title: 'Khách thanh toán chưa vào PT SAP',
      dataIndex: 'pricePaymentNoSap',
      width: 220,
      align: 'right',
    },
    {
      title: 'SAP',
      align: 'center',
      children: [
        {
          title: 'Phải thu',
          dataIndex: 'sapPriceCollect',
          width: 140,
          align: 'right',
        },
        {
          title: 'Phải trả',
          dataIndex: 'sapPricePay',
          width: 140,
          align: 'right',
        },
        {
          title: 'Giữ hộ KH (Q10)',
          dataIndex: 'sapQ10Keep',
          width: 160,
          align: 'right',
        },
        {
          title: 'Nhập tạm (Q10)',
          dataIndex: 'sapQ10Temporary',
          width: 150,
          align: 'right',
        },
      ],
    },
    {
      title: 'MIRO tạm',
      align: 'center',
      children: [
        {
          title: 'Q10',
          dataIndex: 'q10TemporaryMiro',
          width: 100,
          align: 'right',
        },
        {
          title: 'Tiền mặt',
          dataIndex: 'amountTemporaryMiro',
          width: 140,
          align: 'right',
        },
      ],
    },
    { title: 'Tiền tệ', dataIndex: 'unitPrice', width: 90, align: 'center' },
    { title: 'Đơn vị Q10', dataIndex: 'unitQ10', width: 100, align: 'center' },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifyDate',
      width: 160,
      align: 'center',
    },
    {
      title: 'Thao tác',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: any, record: any) => (
        <EyeIcon
          className="cursor-pointer text-blue-500 hover:text-blue-700"
          onClick={() => {
            // Navigate to debt detail page
            navigate(
              `/debt/dms/customer-detail?customerId=${record.customerId}&sapCustomerId=${record.sapCustomerId}&customerName=${encodeURIComponent(record.storeName || '')}&plantId=${record.plantId}`,
            );
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    setLoadingPlant(true);
    PlantApi.getAllPlantFilterHO()
      .then((res: any) => {
        const items = res?.data?.getPlant?.items || [];
        const filtered = items.filter((item: any) => item.id !== '5000');
        setPlantOptions(
          filtered.map((item: any) => ({
            label: item.id + (item.name ? ` - ${item.name}` : ''),
            value: item.id,
          })),
        );
      })
      .finally(() => setLoadingPlant(false));
  }, []);

  const handleChangePlant = useCallback(
    (plantId: string) => {
      // Reset đội khi thay đổi chi nhánh
      form.setFieldValue('teamSaleId', undefined);
      setTeamOptions(initLazySelectData);

      // Load đội theo chi nhánh mới
      if (plantId) {
        setLoadingTeam(true);
        SaleTeamApi.getAllTeamSaleCb({
          pageSize: 50,
          value: '',
          plantId: plantId,
        })
          .then((res: any) => {
            const data = res?.data?.getTeamSales;
            setTeamOptions({
              options:
                data?.items?.map((item: any) => ({
                  label: `${item?.subId} - ${item?.name}`,
                  value: item?.id,
                })) || [],
              totalItems: data?.pageInfo?.totalItems || 0,
            });
          })
          .finally(() => setLoadingTeam(false));
      }
    },
    [form],
  );

  // Gọi API tự động khi vào màn hình
  useEffect(() => {
    const plantId = location.state?.plantId;
    const teamSaleId = location.state?.teamSaleId;

    // Set filter values từ state
    if (plantId) {
      form.setFieldValue('plantId', plantId);
      // Load đội theo chi nhánh từ state
      handleChangePlant(plantId);
    }
    if (teamSaleId) {
      form.setFieldValue('teamSaleId', teamSaleId);
    }

    // Gọi API với filter
    fetchData({
      page: 1,
      plantId: plantId || null,
      teamSaleId: teamSaleId || null,
    });
  }, [location.state, form, handleChangePlant]);

  const handleTeamSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const plantId = form.getFieldValue('plantId');
      setLoadingTeam(true);
      SaleTeamApi.getAllTeamSaleCb({
        ...params,
        plantId: plantId, // Thêm plantId vào params
      } as any)
        .then((res: any) => {
          const data = res?.data?.getTeamSales;
          setTeamOptions({
            options:
              data?.items?.map((item: any) => ({
                label: `${item?.subId} - ${item?.name}`,
                value: item?.id,
              })) || [],
            totalItems: data?.pageInfo?.totalItems || 0,
          });
        })
        .finally(() => setLoadingTeam(false));
    },
    [form],
  );

  const handleScrollCustomersChange = useCallback(
    (params: ILazySelectParams) => {
      setLoadingCustomer(true);
      CustomerApis.getCustomersInCombobox({
        pageSize: params.pageSize,
        searchValue: params.value || '',
      })
        .then((res: any) => {
          const data = res?.data?.getCustomer;
          setCustomerOptions({
            options:
              data?.items?.map((item: any) => ({
                label:
                  (item?.sapCustomerId || item?.id) +
                  (item?.storeName ? ` - ${item?.storeName}` : ''),
                value: item?.sapCustomerId,
              })) || [],
            totalItems: data?.pageInfo?.totalItems || 0,
          });
        })
        .finally(() => setLoadingCustomer(false));
    },
    [],
  );

  useMemo(() => {
    setTitle('Chi tiết công nợ');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Công nợ' },
      { title: 'Chi tiết công nợ' },
    ]);
  }, []);

  const { mutate: fetchData, isPending: loading } = useMutation({
    mutationFn: async (params: any) => {
      console.log(params.compareSAP);
      const take =
        params.pageSize || pageInfo.itemsPerPage || DEFAULT_PAGE_SIZE;
      const input: any = {
        plantId: params.plantId || null,
        teamSaleId: params.teamSaleId || null,
        sapCustomerId: params.customerId || null,
        mainCustomerId: params.parentCustomerId || null,
        comparison: params.compareSAP
          ? params.compareSAP == 'true'
            ? true
            : false
          : null,
        effectiveTimeFrom: params.fromDate || null,
        effectiveTimeTo: params.toDate || null,
        skip: (params.page - 1) * take,
        take,
      };
      const res = await DebtApis.getCustomerDebitComparisonInfo(input);
      return res?.data?.getCustomerDebitComparisonInfo || {};
    },
    onSuccess: (result: any, variables: any) => {
      const page = variables.page || 1;
      const take =
        variables.pageSize || pageInfo.itemsPerPage || DEFAULT_PAGE_SIZE;
      const items = result?.items || [];
      const mapped = items.map((it: any, idx: number) => ({
        _key: `${page}-${take}-${idx}-${it.sapCustomerId}`,
        stt: (page - 1) * take + idx + 1,
        plantId: it.plantId,
        teamSaleId: it.teamSaleId,
        teamName: it.teamName,
        sapCustomerId: it.sapCustomerId,
        customerId: it.customerId,
        storeName: it.storeName,
        mainCustomerId: it.mainCustomerId,
        mainCustomerName: it.mainCustomerName,
        compare: it.comparisonYn === false ? 'Lệch' : 'Khớp',
        goldThreadQ10Traditional: formatNumber(it.goldThreadQ10Traditional),
        dateQ10Traditional: formatNumber(it.dateQ10Traditional),
        amountWagesTraditional: formatNumber(it.amountWagesTraditional),
        dateWagesTraditional: formatNumber(it.dateWagesTraditional),
        amountHighendLimit: formatNumber(it.amountHighendLimit),
        dateHighendLimit: formatNumber(it.dateHighendLimit),
        amountHighendTraditionalLimit: formatNumber(
          it.amountHighendTraditionalLimit,
        ),
        dateHighendTraditionalLimit: formatNumber(
          it.dateHighendTraditionalLimit,
        ),
        dmsCollectDebitAmount: formatNumber(it.dmsCollectDebitAmount),
        dmsCollectDebitQ10: formatNumber(it.dmsCollectDebitQ10),
        dmsPayDebitAmount: formatNumber(it.dmsPayDebitAmount),
        dmsPayDebitQ10: formatNumber(it.dmsPayDebitQ10),
        holdingProductQ10: formatNumber(it.holdingProductQ10),
        returnProductQ10: formatNumber(it.returnProductQ10),
        longestDebtAge: formatNumber(it.longestDebtAge),
        shortestDebtAge: formatNumber(it.shortestDebtAge),
        averageDebtAge: formatNumber(it.averageDebtAge),
        receivedProductPoQ10: formatNumber(it.receivedProductPoQ10),
        pricePaymentNoSap: formatNumber(it.pricePaymentNoSap),
        sapPriceCollect: formatNumber(it.sapPriceCollect),
        sapPricePay: formatNumber(it.sapPricePay),
        sapQ10Keep: formatNumber(it.sapQ10Keep),
        sapQ10Temporary: formatNumber(it.sapQ10Temporary),
        q10TemporaryMiro: formatNumber(it.q10TemporaryMiro),
        amountTemporaryMiro: formatNumber(it.amountTemporaryMiro),
        unitPrice: it.unitPrice,
        unitQ10: it.unitQ10,
        modifyDate: it.modifyDate
          ? dayjs(it.modifyDate).format('DD/MM/YYYY HH:mm:ss')
          : '-',
      }));
      setData(mapped);
      setPageInfo({
        currentPage: result?.pageInfo?.currentPage || page,
        itemsPerPage: result?.pageInfo?.itemsPerPage || take,
        totalItems: result?.pageInfo?.totalItems || mapped.length,
      });
    },
  });

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = form.getFieldsValue();

      const input = {
        plantId: values.plantId || null,
        teamSaleId: values.teamSaleId || null,
        sapCustomerId: values.customerId || null,
        mainCustomerId: values.parentCustomerId || null,
        comparison: values.compareSAP
          ? values.compareSAP == 'true'
            ? true
            : false
          : null,
        effectiveTimeFrom: values.fromDate || null,
        effectiveTimeTo: values.toDate || null,
        skip: 0,
        take: pageInfo.totalItems || 1000,
      };
      const res = await DebtApis.getCustomerDebitComparisonInfo(input);
      const items = res?.data?.getCustomerDebitComparisonInfo?.items || [];
      const exportData = items.map((it: any, idx: number) => ({
        stt: idx + 1,
        plantId: it.plantId,
        teamSaleId: it.teamSaleId,
        teamName: it.teamName,
        sapCustomerId: it.sapCustomerId,
        storeName: it.storeName,
        mainCustomerId: it.mainCustomerId,
        mainCustomerName: it.mainCustomerName,
        compare: it.comparisonYn === false ? 'Khớp' : 'Lệch',
        goldThreadQ10Traditional: formatNumber(it.goldThreadQ10Traditional),
        dateQ10Traditional: formatNumber(it.dateQ10Traditional),
        amountWagesTraditional: formatNumber(it.amountWagesTraditional),
        dateWagesTraditional: formatNumber(it.dateWagesTraditional),
        amountHighendLimit: formatNumber(it.amountHighendLimit),
        dateHighendLimit: formatNumber(it.dateHighendLimit),
        amountHighendTraditionalLimit: formatNumber(
          it.amountHighendTraditionalLimit,
        ),
        dateHighendTraditionalLimit: formatNumber(
          it.dateHighendTraditionalLimit,
        ),
        dmsCollectDebitQ10: formatNumber(it.dmsCollectDebitQ10),
        dmsCollectDebitAmount: formatNumber(it.dmsCollectDebitAmount),
        dmsPayDebitQ10: formatNumber(it.dmsPayDebitQ10),
        dmsPayDebitAmount: formatNumber(it.dmsPayDebitAmount),
        holdingProductQ10: formatNumber(it.holdingProductQ10),
        returnProductQ10: formatNumber(it.returnProductQ10),
        longestDebtAge: formatNumber(it.longestDebtAge),
        shortestDebtAge: formatNumber(it.shortestDebtAge),
        averageDebtAge: formatNumber(it.averageDebtAge),
        receivedProductPoQ10: formatNumber(it.receivedProductPoQ10),
        pricePaymentNoSap: formatNumber(it.pricePaymentNoSap),
        sapPriceCollect: formatNumber(it.sapPriceCollect),
        sapPricePay: formatNumber(it.sapPricePay),
        sapQ10Keep: formatNumber(it.sapQ10Keep),
        sapQ10Temporary: formatNumber(it.sapQ10Temporary),
        q10TemporaryMiro: formatNumber(it.q10TemporaryMiro),
        amountTemporaryMiro: formatNumber(it.amountTemporaryMiro),
        unitPrice: it.unitPrice,
        unitQ10: it.unitQ10,
        modifyDate: it.modifyDate
          ? dayjs(it.modifyDate).format('DD/MM/YYYY HH:mm:ss')
          : '-',
      }));
      exportExcel_v2(columns, exportData, 'ChiTietCongNo');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSearch = (values: any) => {
    fetchData({ ...values, page: 1 });
  };

  const handleTableChange = (pagination: any) => {
    fetchData({
      ...form.getFieldsValue(),
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={openFilter} setIsOpen={setOpenFilter} />
        </Col>
        <Col>
          <ButtonExport loading={exportLoading} onClick={handleExport} />
        </Col>
        <Col>
          <Button
            title="Điều chỉnh"
            fill
            handleOnclick={() => adjustRef.current?.open()}
          />
        </Col>
      </Row>
      {openFilter && (
        <Content>
          <Form layout="vertical" form={form} onFinish={handleSearch}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item name="plantId" label="Chi nhánh">
                  <SelectForm
                    placeholder="Chọn chi nhánh"
                    options={plantOptions}
                    loading={loadingPlant}
                    onChange={handleChangePlant}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="teamSaleId" label="Đội">
                  <LazySelectForm
                    placeholder="Chọn đội"
                    data={teamOptions}
                    loading={loadingTeam}
                    onSelectScrollChange={handleTeamSelectScrollChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="customerId" label="Khách hàng">
                  <LazySelectForm
                    placeholder="Mã khách hàng"
                    data={customerOptions}
                    loading={loadingCustomer}
                    onSelectScrollChange={handleScrollCustomersChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="compareSAP" label="So sánh SAP">
                  <SelectForm
                    placeholder="Chọn So sánh SAP"
                    options={[
                      { label: 'Khớp', value: 'true' },
                      { label: 'Lệch', value: 'false' },
                    ]}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item name="parentCustomerId" label="Khách hàng cha">
                  <InputForm placeholder="Mã khách hàng cha" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="fromDate" label="Từ ngày">
                  <DatePickerForm
                    className="w-full"
                    placeholder="dd/mm/yyyy"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="toDate" label="Đến ngày">
                  <DatePickerForm
                    className="w-full"
                    placeholder="dd/mm/yyyy"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>{/* Cột trống để căn chỉnh */}</Col>
            </Row>
            <Row gutter={[16, 16]} justify="end">
              <Col span={18}>{/* Cột trống để căn chỉnh */}</Col>
              <Col span={6}>
                <div className="flex gap-2 justify-end">
                  <Button
                    title="Hủy"
                    type="button"
                    handleOnclick={() => form.resetFields()}
                  />
                  <Button fill title="Tìm kiếm" type="submit" />
                </div>
              </Col>
            </Row>
          </Form>
        </Content>
      )}
      <Content>
        <Table
          columns={columns as any}
          dataSource={data}
          rowKey={(r: any) => r?._key}
          loading={loading}
          bordered
          pagination={{
            showSizeChanger: true,
            className: '!mb-0 !mt-5',
            pageSizeOptions: ['10', '20', '50'],
            current: pageInfo.currentPage,
            pageSize: pageInfo.itemsPerPage,
            total: pageInfo.totalItems,
            onChange: (page, pageSize) =>
              handleTableChange({ current: page, pageSize }),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Content>
      <AdjustDebtModal ref={adjustRef} />
    </>
  );
}
